import { useTheme } from '@emotion/react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';

import Button from '~/common/components/Buttons/Button/index';
import Flex from '~/common/components/Flex';
import Text from '~/common/components/Texts/Text';
import MUIButton from '~/common/mui/Buttons/MUIButton/MUIButton';

type AddFieldArrayRowButtonProps = {
  onClick?: () => void;
  readOnlyMode?: boolean;
  children?: React.ReactNode;
  variant?: 'secondary' | 'tertiary';
};

export function AddFieldArrayRowButton({
  onClick,
  readOnlyMode,
  children,
  variant = 'tertiary',
}: Readonly<AddFieldArrayRowButtonProps>) {
  const theme = useTheme();
  if (variant == 'tertiary') {
    return (
      <Button
        sx={{
          background: 'none',
          marginTop: '0.5rem',
          padding: '0.25rem',
        }}
        onClick={onClick}
        isDisabled={readOnlyMode}
        tabIndex={readOnlyMode ? -1 : 0}
      >
        <Flex align={'center'} wordBreak={'break-word'}>
          <FontAwesomeIcon
            icon={faPlusSquare}
            color={theme.colors?.blue[600]}
          />
          <Text paddingLeft={'1rem'}>{children}</Text>
        </Flex>
      </Button>
    );
  } else {
    return (
      <MUIButton
        onClick={onClick}
        isDisabled={readOnlyMode}
        tabIndex={readOnlyMode ? -1 : 0}
        startIcon={<FontAwesomeIcon icon={faPlus} />}
        variant="outlined"
      >
        {children}
      </MUIButton>
    );
  }
}

type DeleteFieldArrayRowButtonProps = {
  onClick?: () => void;
  readOnlyMode?: boolean;
  title?: string;
  sx?: Record<string, any>;
  icon?: IconDefinition;
  color?: string;
};

export function DeleteFieldArrayRowButton({
  onClick,
  readOnlyMode,
  title = 'Delete',
  sx,
  icon = faTrash,
  color,
}: Readonly<DeleteFieldArrayRowButtonProps>) {
  const theme = useTheme();

  return (
    <Button
      title={title}
      sx={{
        padding: 0,
        alignSelf: 'flex-end',
        background: 'none',
        marginBottom: '0.5rem',
        ...sx,
      }}
      isDisabled={readOnlyMode}
      tabIndex={readOnlyMode ? -1 : 0}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} color={color ?? theme.colors?.red[600]} />
    </Button>
  );
}
